import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessPhotosDto,
  AssistantStatus,
  CaseSummary,
  CaseSummaryDto,
  ConfirmPhotoAssessmentDto,
  CorrespondenceDraft,
  DraftCorrespondenceDto,
  EscalationRisk,
  PhotoAssessmentResult,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { filesKeys } from './files-api';

/**
 * The AI case assistant on one file. Every route is gated server-side on
 * `ai:assist` at the file and on the municipality having turned it on; the
 * panel asks for the status first and shows nothing when it is off.
 *
 * The model-backed calls are mutations, not queries: each one costs a request
 * to the model and writes an audit row, so it runs when the officer asks and
 * never on a refetch.
 */
export const assistantKeys = {
  status: (fileId: string) => ['assistant', fileId, 'status'] as const,
  risk: (fileId: string) => ['assistant', fileId, 'risk'] as const,
  facts: (fileId: string) => ['assistant', fileId, 'facts'] as const,
};

const base = (fileId: string) => `/files/${fileId}/assistant`;

export function useAssistantStatus(fileId: string, enabled = true) {
  return useQuery({
    queryKey: assistantKeys.status(fileId),
    queryFn: async () => (await api.get<AssistantStatus>(base(fileId))).data,
    enabled: !!fileId && enabled,
    staleTime: 60_000,
  });
}

export function useEscalationRisk(fileId: string, enabled = true) {
  return useQuery({
    queryKey: assistantKeys.risk(fileId),
    queryFn: async () => (await api.get<EscalationRisk>(`${base(fileId)}/escalation-risk`)).data,
    enabled: !!fileId && enabled,
  });
}

/** The facts half of the summary — no model call, safe to load on open. */
export function useCaseFacts(fileId: string, enabled = true) {
  return useQuery({
    queryKey: assistantKeys.facts(fileId),
    queryFn: async () =>
      (
        await api.post<CaseSummary>(`${base(fileId)}/summary`, {
          mode: 'facts',
        } satisfies CaseSummaryDto)
      ).data,
    enabled: !!fileId && enabled,
  });
}

export function useGenerateSummary(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CaseSummaryDto) =>
      (await api.post<CaseSummary>(`${base(fileId)}/summary`, { ...dto, mode: 'full' })).data,
    // The facts come back with the narrative; keep the card in step.
    onSuccess: (data) => qc.setQueryData(assistantKeys.facts(fileId), data),
    // Every model call is a step on the file's trail, answered or not.
    onSettled: () => void qc.invalidateQueries({ queryKey: filesKeys.activity(fileId) }),
  });
}

export function useDraftCorrespondence(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: DraftCorrespondenceDto) =>
      (await api.post<CorrespondenceDraft>(`${base(fileId)}/draft`, dto)).data,
    onSettled: () => void qc.invalidateQueries({ queryKey: filesKeys.activity(fileId) }),
  });
}

export function useAssessPhotos(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: AssessPhotosDto) =>
      (await api.post<PhotoAssessmentResult>(`${base(fileId)}/photo-assessment`, dto)).data,
    onSettled: () => void qc.invalidateQueries({ queryKey: filesKeys.activity(fileId) }),
  });
}

export function useConfirmPhotoAssessment(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: ConfirmPhotoAssessmentDto) =>
      (await api.post(`${base(fileId)}/photo-assessment/confirm`, dto)).data,
    // The confirmation is a step on the file's trail.
    onSuccess: () => void qc.invalidateQueries({ queryKey: filesKeys.activity(fileId) }),
  });
}
